import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ApiClient from 'Api/ApiClient';
import AuthContext from 'context/AuthContext';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { UrlHelpers } from 'urlHelpers/urlHelper';

import Loader from 'components/Loader/Loader';

const STEPS = {
  FORM: 'form',
  ARE_YOU_SURE: 'areYouSure',
  RESULT: 'result',
};

const LOCK_REASONS = [
  'Fraud investigation',
  'Account-takeover suspicion',
  'Customer freeze request',
  'Others',
];

const dialogPaperSx = {
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(16, 42, 67, 0.18)',
  p: 1,
  // Smaller outer margin on phones so the dialog keeps most of the screen width.
  m: { xs: 2, sm: 4 },
  width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
  maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
};

const titleSx = {
  fontSize: { xs: '20px !important', sm: '24px !important' },
  fontWeight: 700,
  color: '#0F172A',
};

// Stack the buttons full width on phones (primary action on top), side by side from tablet up.
const actionsSx = {
  mt: { xs: 3, sm: 4 },
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  justifyContent: 'space-between',
  gap: 1.5,
};

const actionButtonWidth = { xs: '100%', sm: 'auto' };

const labelSx = {
  fontSize: '12px !important',
  fontWeight: 700,
  letterSpacing: '0.6px',
  color: '#5B7184',
  textTransform: 'uppercase',
  mb: 1,
};

const requiredMarkSx = { color: '#D71717', ml: 0.5 };

const RequiredLabel = ({ children }) => (
  <Typography sx={labelSx}>
    {children}
    <Box component="span" sx={requiredMarkSx}>
      *
    </Box>
  </Typography>
);

RequiredLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#D9DEE5' },
    '&:hover fieldset': { borderColor: '#B7C0CC' },
    '&.Mui-focused fieldset': { borderColor: '#70166F' },
  },
  '& .MuiInputBase-input': { fontSize: '15px', color: '#313131', py: '12px' },
  '& .MuiInputBase-input::placeholder': { fontSize: '15px', color: '#98A2B3', opacity: 1 },
};

const apiClient = new ApiClient();

function LockUserDialog({ open, customer, onClose, onSuccess }) {
  const { initiateUiResponse } = useContext(AuthContext);
  const [step, setStep] = useState(STEPS.FORM);
  const [reason, setReason] = useState(LOCK_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const name = customer?.name || customer?.username || 'This customer';

  const handleExited = () => {
    setStep(STEPS.FORM);
    setReason(LOCK_REASONS[0]);
    setNotes('');
    setSubmitting(false);
    setSuccess(false);
    setErrorMessage('');
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  console.log('------------', customer?.username);

  const handleLock = async () => {
    setSubmitting(true);
    try {
      await apiClient.put(
        `${UrlHelpers.updateUser(customer?.preferredusernm)}?status=1`,
        {},
        {
          headers: {
            'x-api-key': initiateUiResponse.REACT_APP_X_API_KEY_ADMIN,
            appid: 'fis',
          },
        },
        initiateUiResponse?.REACT_APP_ADMIN_TOKEN
      );
      setSuccess(true);
      setErrorMessage('');
      onSuccess?.();
    } catch {
      setSuccess(false);
      setErrorMessage('We are unable to process your request. Please try again later.');
    } finally {
      setSubmitting(false);
      setStep(STEPS.RESULT);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Loader open={submitting} />
      {step === STEPS.FORM && (
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography sx={titleSx}>Lock user (Admin Suspend)</Typography>
          <Box sx={{ mt: { xs: 2, sm: 3 } }}>
            <RequiredLabel>Reason</RequiredLabel>
            <Select
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{
                borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9DEE5' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B7C0CC' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#70166F' },
                '& .MuiSelect-select': { fontSize: '15px', color: '#313131', py: '12px' },
              }}
            >
              {LOCK_REASONS.map((option) => (
                <MenuItem key={option} value={option} sx={{ fontSize: '15px' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ mt: 3 }}>
            <RequiredLabel>Notes (written to audit)</RequiredLabel>
            <TextField
              fullWidth
              sx={fieldSx}
              placeholder="e.g., Fraud ticket #INC-48211"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>

          <Box sx={actionsSx}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disableRipple
              sx={{
                width: actionButtonWidth,
                minWidth: 100,
                height: 44,
                borderRadius: '8px',
                background: '#ffffff',
                color: '#000000',
                textTransform: 'none',
                border: '1px solid #000000',
                fontWeight: 600,
                boxShadow: 'none',
                fontSize: '15px !important',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: '#474747',
                  boxShadow: 'none',
                  border: 'none',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleLock}
              variant="contained"
              disableRipple
              disabled={submitting || !notes.trim()}
              sx={{
                width: actionButtonWidth,
                height: 44,
                px: 3,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#000000',
                border: '1px solid #000000',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px !important',
                boxShadow: 'none',

                '&:hover': {
                  backgroundColor: '#ffffff',
                  boxShadow: 'none',
                  border: '1px solid #000000',
                  color: '#000000',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#C7C7C7',
                  border: '1px solid #C7C7C7',
                  color: '#ffffff',
                },
              }}
            >
              Confirm lock
            </Button>
          </Box>
        </DialogContent>
      )}

      {step === STEPS.RESULT && (
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: success ? '#DEF3E4' : '#FDECEC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
            }}
          >
            {success ? (
              <CheckIcon sx={{ color: '#1B9E4B', fontSize: 34 }} />
            ) : (
              <CloseIcon sx={{ color: '#D71717', fontSize: 34 }} />
            )}
          </Box>

          {success && <Typography sx={{ ...titleSx, mt: 2 }}>Admin Lock applied</Typography>}
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: '15px !important', sm: '17px !important' },
              overflowWrap: 'anywhere',
              color: success ? '#475569' : '#d71714',
            }}
          >
            {success
              ? `${name} is now under Admin Suspend. Sign-in is blocked until the Admin Lock is cleared from this portal.`
              : errorMessage ||
                `We were unable to apply the Admin Lock for ${name}. Please try again.`}
          </Typography>

          <Box sx={{ mt: { xs: 3, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={handleClose}
              variant="contained"
              disableRipple
              sx={{
                minWidth: 120,
                height: 44,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#6B21D8',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px !important',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#5A18BC', boxShadow: 'none' },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

LockUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  customer: PropTypes.shape({
    name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    registeredfrom: PropTypes.string,
    preferredusernm: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

LockUserDialog.defaultProps = {
  customer: null,
  onSuccess: null,
};

export default LockUserDialog;
